
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { Star, User, MessageCircle } from 'lucide-react';

interface Rating {
  id: string;
  restaurant_rating: number;
  restaurant_comment: string;
  created_at: string;
  customer: {
    full_name: string;
  };
  order: {
    id: string;
  };
}

interface RestaurantRatingsViewProps {
  restaurantId: string;
}

const RestaurantRatingsView = ({ restaurantId }: RestaurantRatingsViewProps) => {
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [averageRating, setAverageRating] = useState(0);
  const [ratingCounts, setRatingCounts] = useState<{ [key: number]: number }>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRatings();
  }, [restaurantId]);

  const fetchRatings = async () => {
    try {
      const { data, error } = await supabase
        .from('ratings')
        .select(`
          *,
          customer:profiles!ratings_customer_id_fkey(full_name),
          order:orders!ratings_order_id_fkey(id)
        `)
        .eq('restaurant_id', restaurantId)
        .not('restaurant_rating', 'is', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching ratings:', error);
      } else {
        setRatings(data || []);
        
        if (data && data.length > 0) {
          // Calculate average rating
          const sum = data.reduce((total, rating) => total + rating.restaurant_rating, 0);
          setAverageRating(sum / data.length);
          
          // Calculate rating distribution
          const counts: { [key: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
          data.forEach((rating) => {
            counts[rating.restaurant_rating] = (counts[rating.restaurant_rating] || 0) + 1;
          });
          setRatingCounts(counts);
        }
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`h-4 w-4 ${
          index < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (ratings.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Star className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Ratings Yet</h3>
          <p className="text-gray-600">You haven't received any customer ratings yet. Keep providing great service!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Rating Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-4xl font-bold">{averageRating.toFixed(1)}</span>
              <Star className="h-8 w-8 fill-yellow-400 text-yellow-400" />
            </div>
            <p className="text-gray-600">Average Rating</p>
            <p className="text-sm text-gray-500">{ratings.length} total ratings</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <h4 className="font-semibold mb-4">Rating Distribution</h4>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((stars) => (
                <div key={stars} className="flex items-center gap-2">
                  <span className="text-sm w-4">{stars}</span>
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: ratings.length > 0 ? `${((ratingCounts[stars] || 0) / ratings.length) * 100}%` : '0%'
                      }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-8">{ratingCounts[stars] || 0}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Individual Ratings */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Reviews</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {ratings.map((rating) => (
              <div key={rating.id} className="border-b pb-4 last:border-b-0">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="font-medium text-sm">
                        {rating.customer?.full_name || 'Anonymous Customer'}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      Order #{rating.order?.id?.slice(0, 8)}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    {renderStars(rating.restaurant_rating)}
                  </div>
                </div>
                
                {rating.restaurant_comment && (
                  <div className="flex items-start gap-2 mt-2">
                    <MessageCircle className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">{rating.restaurant_comment}</p>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-2">
                  {new Date(rating.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RestaurantRatingsView;
